import unescape from 'lodash/unescape';
import { CSSProperties, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';
import { BreakpointsContext } from '../../../../../common/contexts/BreakpointsContext';
import Carousel from '../../../../../common/widgets/Carousel';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import PopoverDialog from '../../../../../common/widgets/PopoverDialog';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { PlatformBenefitTypes, TopLevelCategories } from '../../../enums';
import { rewriteImageUrl } from '../../../helpers';
import { SmartSpendingItem, RedirectTypes } from '../../../models/Categories';
import { resources } from '../../../resources/resources';
import { CarouselsContext } from '../../../contexts/CarouselsProvider';
import { ProductTypeSlugs, TopLevelCategoryBasePath } from '../../../constants';
import { TFunction } from 'i18next';

const formatTaxRate = (rate: number) => Number(rate.toFixed(2)).toString();

const getTaxRange = (
  item: SmartSpendingItem,
  translate: TFunction,
  isMobile: boolean,
) => {
  if (item.minTaxRate != null && item.maxTaxRate != null) {
    if (item.minTaxRate !== item.maxTaxRate) {
      return isMobile
        ? `${translate(
            resources.home.smartSpending.taxRangePrefix,
          )} ${formatTaxRate(item.minTaxRate)}%`
        : `${translate(
            resources.home.smartSpending.taxRangePrefix,
          )} ${formatTaxRate(item.minTaxRate)}% ${translate(
            resources.home.smartSpending.taxRangeSufix,
          )}`;
    } else {
      return `${formatTaxRate(item.minTaxRate)}% ${translate(
        resources.home.smartSpending.taxRangeSufix,
      )}`;
    }
  }

  return undefined;
};

const getCategoryUrl = (item: SmartSpendingItem) => {
  switch (item.type) {
    case RedirectTypes.FlexibleBenefits:
      return item.goTo && item.goTo !== TopLevelCategories.FlexibleBenefits
        ? `/flexible-benefits/${item.goTo}`
        : TopLevelCategoryBasePath[TopLevelCategories.FlexibleBenefits];
    case RedirectTypes.ClubBenefits:
      return item.goTo && item.goTo !== TopLevelCategories.BenefitClub
        ? `/club-benefits/${item.goTo}`
        : TopLevelCategoryBasePath[TopLevelCategories.BenefitClub];
    case RedirectTypes.Browse:
      return ``;
    case RedirectTypes.Product:
      return `/products/${item.goTo}`;
    case RedirectTypes.EdenredCards:
      return `/my-account/cards`;
    case RedirectTypes.ProductType:
      return `/products/type/${ProductTypeSlugs[item.goTo ?? 0]}`;
    case RedirectTypes.MyBudget:
      return '/my-account/dashboard';
    case RedirectTypes.Cashback:
      return item.goTo && item.goTo !== TopLevelCategories.Cashback
        ? `/cashback/${item.goTo}`
        : TopLevelCategoryBasePath[TopLevelCategories.Cashback];
    case RedirectTypes.DiscountVouchers:
      return item.goTo && item.goTo !== TopLevelCategories.DiscountVouchers
        ? `/discount-vouchers/${item.goTo}`
        : TopLevelCategoryBasePath[TopLevelCategories.DiscountVouchers];
    case RedirectTypes.ActiveBenefits:
      return '/my-account/my-benefits';
    default:
      return undefined;
  }
};

export const HomeSmartSpending = () => {
  const { t: translate } = useTranslation();
  const { smartSpendings, loading } = useContext(CarouselsContext);
  const { customerInfo } = useContext(CustomerInfoContext);
  const { isOnlyDesktop, isOnlyMobile } = useContext(BreakpointsContext);
  const items = smartSpendings;

  const itemCount = items?.length ?? 0;
  const showDesktopPagination = itemCount > 6;

  const hasBudget =
    customerInfo.platformType === PlatformBenefitTypes.FlexibleBenefits;
  const useBudgetTitle = isOnlyDesktop && hasBudget;
  const smartSpendingTitle = useBudgetTitle
    ? translate(resources.home.title.smartSpendingBudget)
    : translate(resources.home.title.smartSpending);

  if (itemCount === 0) {
    return null;
  }

  const renderItem = (item: SmartSpendingItem) => {
    const url = getCategoryUrl(item);
    const taxRange = getTaxRange(item, translate, isOnlyMobile);
    const key = `${item.type}-${item.goTo}`;
    const content = (
      <>
        <div className="smart-spending-image">
          {item.imagePath && (
            <img
              src={rewriteImageUrl(item.imagePath)}
              alt={unescape(item.name || '')}
              draggable={false}
            />
          )}
        </div>
        <div className="smart-spending-details">
          <div className="smart-spending-name">{unescape(item.name || '')}</div>
          {taxRange && (
            <div className="smart-spending-tax-range">{taxRange}</div>
          )}
        </div>
      </>
    );

    return url ? (
      <Link
        className="smart-spending-item"
        to={url}
        key={key}
        draggable={false}
      >
        {content}
      </Link>
    ) : (
      <div className="smart-spending-item" key={key}>
        {content}
      </div>
    );
  };

  return (
    <section className="page-section smart-spending-section">
      <h2 className="section-title">
        <span className="smart-spending-title-content">
          <span>{smartSpendingTitle}</span>
          {hasBudget && (
            <PopoverDialog
              className="checkout-info-dropdown"
              placement={isOnlyMobile ? 'top' : 'right'}
              maxWidth={isOnlyMobile ? 260 : 350}
              closeFromContent
              type="hover"
              content={
                <span>{translate(resources.home.smartSpending.tooltip)}</span>
              }
            >
              <button
                type="button"
                className="smart-spending-info-button"
                aria-label={translate(resources.home.smartSpending.tooltip)}
              >
                <Icons.InfoSummary />
              </button>
            </PopoverDialog>
          )}
        </span>
      </h2>

      <Loader
        loading={loading.smartSpendings}
        loader={<Skeleton height={120} />}
      >
        {isOnlyDesktop && itemCount <= 6 ? (
          <div
            className="smart-spending-list"
            style={
              {
                '--smart-spending-count': itemCount,
              } as CSSProperties
            }
          >
            {items?.map(renderItem)}
          </div>
        ) : (
          <div
            className={`smart-spending-carousel edn-carousel edn-carousel-dots no-dots-tablet no-dots-mobile${
              showDesktopPagination
                ? ' smart-spending-carousel--desktop-pagination'
                : ''
            }`}
          >
            <Carousel
              dots={showDesktopPagination}
              infinite={false}
              speed={500}
              slidesToShow={6}
              slidesToScroll={showDesktopPagination ? 6 : 1}
              arrows={false}
              swipe={showDesktopPagination}
              draggable={showDesktopPagination}
              touchMove={showDesktopPagination}
              loading={loading.smartSpendings}
              responsive={[
                {
                  breakpoint: 992,
                  settings: {
                    dots: false,
                    slidesToShow: 4,
                    slidesToScroll: Math.min(itemCount, 4),
                    swipe: itemCount > 4,
                    draggable: itemCount > 4,
                    touchMove: itemCount > 4,
                  },
                },
                {
                  breakpoint: 480,
                  settings: {
                    dots: false,
                    slidesToShow: Math.min(itemCount, 4),
                    slidesToScroll: Math.min(itemCount, 4),
                    swipe: itemCount > 4,
                    draggable: itemCount > 4,
                    touchMove: itemCount > 4,
                  },
                },
                {
                  breakpoint: 360,
                  settings: {
                    dots: false,
                    slidesToShow: 3,
                    slidesToScroll: Math.min(itemCount, 3),
                    swipe: itemCount > 3,
                    draggable: itemCount > 3,
                    touchMove: itemCount > 3,
                  },
                },
              ]}
            >
              {items?.map(renderItem)}
            </Carousel>
          </div>
        )}
      </Loader>
    </section>
  );
};
